import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useParams, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useOfflineMutation } from '../../../hooks/useOfflineMutation';
import { logAccess } from '../../../lib/accessLogs';
import BackButton from '../../../components/feature/BackButton';
import DossierRoseForm from './components/DossierRoseForm';
import HistoriqueSection from './components/HistoriqueSection';

interface Patient {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string;
  telephone: string;
  email: string;
  adresse: string;
  groupe_sanguin: string;
  allergies: string;
  maladies_chroniques: string;
  antecedents_medicaux: string;
  antecedents_chirurgicaux: string;
  medicaments_actuels: string;
  personne_contact_nom: string;
  personne_contact_telephone: string;
  notes_medicales: string;
  numero_dossier: string;
  sexe: string;
}

interface ConsultationLight {
  id: string;
  date_consultation: string;
  statut: string;
}

type TabType = 'informations' | 'dossier_rose' | 'historique' | 'partages';

export default function DossierPatientPage() {
  const { user, role } = useAuth();
  const [searchParams] = useSearchParams();
  const { patientId: patientIdFromUrl } = useParams();
  const patientIdFromQuery = searchParams.get('id');
  const patientId = patientIdFromUrl || patientIdFromQuery;
  const navigate = useNavigate();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('informations');
  const [showDossierRose, setShowDossierRose] = useState(false);
  const [medecinSpecialite, setMedecinSpecialite] = useState('');
  const [recentConsultations, setRecentConsultations] = useState<ConsultationLight[]>([]);
  const [showFicheVisitePicker, setShowFicheVisitePicker] = useState(false);

  // Mode édition
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<Partial<Patient>>();
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const { mutate } = useOfflineMutation<any>();

  useEffect(() => {
    if (patientId) {
      loadPatientData(patientId);
      loadRecentConsultations(patientId);
    } else {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    checkDossierRoseAccess();
  }, [user, role]);

  const checkDossierRoseAccess = async () => {
    if (!user || !role) return;
    if (role === 'sage_femme' || role === 'super_admin' || role === 'admin' || role === 'secretaire') {
      setShowDossierRose(true);
      return;
    }
    if (role === 'medecin') {
      const { data } = await supabase
        .from('medecins')
        .select('specialite')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data?.specialite === 'Gynécologie' || data?.specialite === 'Gynécologie-obstétrique') {
        setShowDossierRose(true);
        setMedecinSpecialite(data.specialite);
      }
    }
  };

  const loadPatientData = async (id: string) => {
    try {
      setLoading(true);

      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('*')
        .eq('id', id)
        .single();

      if (patientError) {
        console.error('Erreur Supabase:', patientError);
        throw patientError;
      }

      if (!patientData) {
        setPatient(null);
      } else {
        setPatient(patientData);

        if (user && role) {
          await logAccess({
            userId: user.id,
            userRole: role,
            actionType: 'VIEW',
            resourceType: 'DOSSIER_MEDICAL',
            resourceId: id,
            patientId: id,
            details: {
              patient_nom: `${patientData.prenom} ${patientData.nom}`,
              action: 'Consultation du dossier médical'
            }
          });
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setPatient(null);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentConsultations = async (id: string) => {
    const { data } = await supabase
      .from('consultations')
      .select('id, date_consultation, statut')
      .eq('patient_id', id)
      .order('date_consultation', { ascending: false })
      .limit(20);
    setRecentConsultations(data || []);
  };

  const startEditing = () => {
    if (!patient) return;
    setEditData({
      nom: patient.nom,
      prenom: patient.prenom,
      date_naissance: patient.date_naissance,
      telephone: patient.telephone || '',
      email: patient.email || '',
      adresse: patient.adresse || '',
      groupe_sanguin: patient.groupe_sanguin || '',
      allergies: patient.allergies || '',
      maladies_chroniques: patient.maladies_chroniques || '',
      antecedents_medicaux: patient.antecedents_medicaux || '',
      antecedents_chirurgicaux: patient.antecedents_chirurgicaux || '',
      medicaments_actuels: patient.medicaments_actuels || '',
      personne_contact_nom: patient.personne_contact_nom || '',
      personne_contact_telephone: patient.personne_contact_telephone || '',
      notes_medicales: patient.notes_medicales || '',
      sexe: patient.sexe || '',
    });
    setSaveSuccess(false);
    setSaveError('');
    setEditMode(true);
  };

  const cancelEditing = () => {
    setEditMode(false);
    setEditData({});
    setSaveError('');
  };

  const handleSave = async () => {
    if (!patient || !editData) return;

    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    const online = navigator.onLine;

    try {
      await mutate({
        table: 'patients',
        operation: 'UPDATE',
        recordId: patient.id,
        data: {
          nom: editData.nom,
          prenom: editData.prenom,
          date_naissance: editData.date_naissance,
          telephone: editData.telephone || null,
          email: editData.email || null,
          adresse: editData.adresse || null,
          groupe_sanguin: editData.groupe_sanguin || null,
          allergies: editData.allergies || null,
          maladies_chroniques: editData.maladies_chroniques || null,
          antecedents_medicaux: editData.antecedents_medicaux || null,
          antecedents_chirurgicaux: editData.antecedents_chirurgicaux || null,
          medicaments_actuels: editData.medicaments_actuels || null,
          personne_contact_nom: editData.personne_contact_nom || null,
          personne_contact_telephone: editData.personne_contact_telephone || null,
          notes_medicales: editData.notes_medicales || null,
          sexe: editData.sexe || null,
        },
        expectedUpdatedAt: (patient as any).updated_at || null,
        metadata: { summary: `MàJ dossier — ${patient.prenom} ${patient.nom}` },
        onlineExecutor: async () => {
          const { error } = await supabase
            .from('patients')
            .update({
              nom: editData.nom,
              prenom: editData.prenom,
              date_naissance: editData.date_naissance,
              telephone: editData.telephone || null,
              email: editData.email || null,
              adresse: editData.adresse || null,
              groupe_sanguin: editData.groupe_sanguin || null,
              allergies: editData.allergies || null,
              maladies_chroniques: editData.maladies_chroniques || null,
              antecedents_medicaux: editData.antecedents_medicaux || null,
              antecedents_chirurgicaux: editData.antecedents_chirurgicaux || null,
              medicaments_actuels: editData.medicaments_actuels || null,
              personne_contact_nom: editData.personne_contact_nom || null,
              personne_contact_telephone: editData.personne_contact_telephone || null,
              notes_medicales: editData.notes_medicales || null,
              sexe: editData.sexe || null,
            })
            .eq('id', patient.id);

          if (error) throw error;
          return { id: patient.id };
        },
      });

      try { await loadPatientData(patient.id); } catch (_) { /* offline — ok */ }
      setSaveSuccess(true);
      setEditMode(false);

      if (online && user && role) {
        await logAccess({
          userId: user.id,
          userRole: role,
          actionType: 'UPDATE',
          resourceType: 'DOSSIER_MEDICAL',
          resourceId: patient.id,
          patientId: patient.id,
          details: {
            patient_nom: `${editData.prenom} ${editData.nom}`,
            action: 'Modification du dossier médical'
          }
        });
      }

      if (!online) {
        setSaveSuccess(true);
        setSaveError('');
        setTimeout(() => setSaveSuccess(false), 5000);
      } else {
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      setSaveError(error instanceof Error ? error.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleFicheVisiteGlobal = () => {
    if (recentConsultations.length === 0) return;
    if (recentConsultations.length === 1) {
      navigate(`/medecin/fiche-visite?patientId=${patientId}&consultationId=${recentConsultations[0].id}`);
      return;
    }
    setShowFicheVisitePicker(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin"></i>
          <p className="mt-4 text-gray-600">Chargement du dossier...</p>
        </div>
      </div>
    );
  }

  if (!patientId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-red-500"></i>
          <p className="mt-4 text-xl font-semibold text-gray-900">ID patient manquant</p>
          <p className="mt-2 text-gray-600">Aucun identifiant de patient n'a été fourni</p>
          <button
            onClick={() => navigate('/medecin/rendez-vous')}
            className="mt-6 px-6 py-2 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark whitespace-nowrap cursor-pointer"
          >
            Retour aux rendez-vous
          </button>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-red-500"></i>
          <p className="mt-4 text-xl font-semibold text-gray-900">Patient non trouvé</p>
          <p className="mt-2 text-gray-600">ID du patient : {patientId}</p>
          <button
            onClick={() => navigate('/medecin/rendez-vous')}
            className="mt-6 px-6 py-2 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark whitespace-nowrap cursor-pointer"
          >
            Retour aux rendez-vous
          </button>
        </div>
      </div>
    );
  }

  const age = new Date().getFullYear() - new Date(patient.date_naissance).getFullYear();

  const currentData = editMode ? editData : patient;

  const inputClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue focus:outline-none transition-colors text-sm bg-white";
  const labelClass = "block text-sm text-gray-600 mb-1 font-medium";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <BackButton />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {patient.prenom} {patient.nom}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {age} ans • {patient.groupe_sanguin || 'Groupe sanguin non renseigné'} • N° dossier : {patient.numero_dossier || '-'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {(role === 'medecin' || role === 'super_admin') && (
                <button
                  onClick={handleFicheVisiteGlobal}
                  className="px-5 py-2.5 bg-clinic-blue/10 text-clinic-blue-dark border-2 border-clinic-blue/30 rounded-xl hover:bg-clinic-blue/15 transition-all font-semibold text-sm whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                                    Fiche de visite
                </button>
              )}
              {!editMode && activeTab !== 'dossier_rose' && (
                <>
                  <button
                    onClick={startEditing}
                    className="px-5 py-2.5 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue-dark transition-all font-semibold text-sm whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                                        Modifier le dossier
                  </button>
                </>
              )}
            </div>
          </div>

          {saveSuccess && (
            <div className="mt-4 p-3 bg-green-50 border-2 border-green-200 text-green-700 rounded-xl text-sm">
              <i className="ri-checkbox-circle-line mr-2"></i>
              Dossier modifié avec succès !
            </div>
          )}

          {saveError && (
            <div className="mt-4 p-3 bg-red-50 border-2 border-red-200 text-red-700 rounded-xl text-sm">
              <i className="ri-error-warning-line mr-2"></i>
              {saveError}
            </div>
          )}

          {/* Toggle Dossier Général / Dossier Rose */}
          {showDossierRose && (
            <div className="mt-5 flex justify-center">
              <div className="inline-flex bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => { setActiveTab('informations'); setEditMode(false); }}
                  className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                    activeTab !== 'dossier_rose'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                                    Dossier Général
                </button>
                <button
                  onClick={() => { setActiveTab('dossier_rose'); setEditMode(false); }}
                  className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                    activeTab === 'dossier_rose'
                      ? 'bg-white text-rose-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                                    Dossier Rose
                </button>
                <button
                  onClick={() => setActiveTab('historique')}
                  className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                    activeTab === 'historique'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                                    Historique
                </button>
                <button
                  onClick={() => setActiveTab('partages')}
                  className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                    activeTab === 'partages'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                                    Partages (0)
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Old Tabs - only shown when dossier rose is not available */}
      {!showDossierRose && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex gap-8">
              <button
                onClick={() => setActiveTab('informations')}
                className={`py-4 px-2 border-b-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'informations'
                    ? 'border-clinic-blue text-clinic-blue'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                                Dossier Général
              </button>
              <button
                onClick={() => setActiveTab('historique')}
                className={`py-4 px-2 border-b-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'historique'
                    ? 'border-clinic-blue text-clinic-blue'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                                Historique des Consultations
              </button>
              <button
                onClick={() => setActiveTab('partages')}
                className={`py-4 px-2 border-b-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'partages'
                    ? 'border-clinic-blue text-clinic-blue'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                                Partages (0)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'informations' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Informations Personnelles */}
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                Informations Personnelles
              </h2>
              <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                <div>
                  <p className={labelClass}>Prénom</p>
                  {editMode ? (
                    <input
                      type="text"
                      value={currentData.prenom || ''}
                      onChange={(e) => setEditData({ ...editData, prenom: e.target.value })}
                      className={inputClass}
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{patient.prenom}</p>
                  )}
                </div>
                <div>
                  <p className={labelClass}>Nom</p>
                  {editMode ? (
                    <input
                      type="text"
                      value={currentData.nom || ''}
                      onChange={(e) => setEditData({ ...editData, nom: e.target.value })}
                      className={inputClass}
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{patient.nom}</p>
                  )}
                </div>
                <div>
                  <p className={labelClass}>Sexe</p>
                  {editMode ? (
                    <select
                      value={currentData.sexe || ''}
                      onChange={(e) => setEditData({ ...editData, sexe: e.target.value })}
                      className={inputClass}
                    >
                      <option value="">Non renseigné</option>
                      <option value="M">Masculin</option>
                      <option value="F">Féminin</option>
                    </select>
                  ) : (
                    <p className="text-gray-900 font-medium">
                      {patient.sexe === 'M' ? 'Masculin' : patient.sexe === 'F' ? 'Féminin' : '-'}
                    </p>
                  )}
                </div>
                <div>
                  <p className={labelClass}>Date de naissance</p>
                  {editMode ? (
                    <input
                      type="date"
                      value={currentData.date_naissance || ''}
                      onChange={(e) => setEditData({ ...editData, date_naissance: e.target.value })}
                      className={inputClass}
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">
                      ({age} ans) {new Date(patient.date_naissance).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>
                <div>
                  <p className={labelClass}>Téléphone</p>
                  {editMode ? (
                    <input
                      type="text"
                      value={currentData.telephone || ''}
                      onChange={(e) => setEditData({ ...editData, telephone: e.target.value })}
                      className={inputClass}
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{patient.telephone || '-'}</p>
                  )}
                </div>
                <div>
                  <p className={labelClass}>N° Dossier</p>
                  <p className="text-gray-900 font-medium">{patient.numero_dossier || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className={labelClass}>Email</p>
                  {editMode ? (
                    <input
                      type="email"
                      value={currentData.email || ''}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      className={inputClass}
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{patient.email}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <p className={labelClass}>Adresse</p>
                  {editMode ? (
                    <input
                      type="text"
                      value={currentData.adresse || ''}
                      onChange={(e) => setEditData({ ...editData, adresse: e.target.value })}
                      className={inputClass}
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{patient.adresse || '-'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Informations Médicales */}
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                Informations Médicales
              </h2>
              <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                <div>
                  <p className={labelClass}>Groupe sanguin</p>
                  {editMode ? (
                    <select
                      value={currentData.groupe_sanguin || ''}
                      onChange={(e) => setEditData({ ...editData, groupe_sanguin: e.target.value })}
                      className={inputClass}
                    >
                      <option value="">Non renseigné</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  ) : (
                    <p className="text-gray-900 font-medium">{patient.groupe_sanguin || 'Non renseigné'}</p>
                  )}
                </div>
                <div>
                  <p className={labelClass}>Allergies</p>
                  {editMode ? (
                    <textarea
                      value={currentData.allergies || ''}
                      onChange={(e) => setEditData({ ...editData, allergies: e.target.value })}
                      className={inputClass + ' resize-none'}
                      rows={2}
                      maxLength={500}
                    />
                  ) : (
                    <p className="text-gray-900">{patient.allergies || '-'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Antécédents Médicaux */}
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Antécédents Médicaux</h2>
              <div className="space-y-4">
                <div>
                  <p className={labelClass}>Maladies chroniques</p>
                  {editMode ? (
                    <textarea
                      value={currentData.maladies_chroniques || ''}
                      onChange={(e) => setEditData({ ...editData, maladies_chroniques: e.target.value })}
                      className={inputClass + ' resize-none'}
                      rows={2}
                      maxLength={500}
                    />
                  ) : (
                    <p className="text-gray-900">{patient.maladies_chroniques || '-'}</p>
                  )}
                </div>
                <div>
                  <p className={labelClass}>Antécédents médicaux</p>
                  {editMode ? (
                    <textarea
                      value={currentData.antecedents_medicaux || ''}
                      onChange={(e) => setEditData({ ...editData, antecedents_medicaux: e.target.value })}
                      className={inputClass + ' resize-none'}
                      rows={2}
                      maxLength={500}
                    />
                  ) : (
                    <p className="text-gray-900">{patient.antecedents_medicaux || '-'}</p>
                  )}
                </div>
                <div>
                  <p className={labelClass}>Antécédents chirurgicaux</p>
                  {editMode ? (
                    <textarea
                      value={currentData.antecedents_chirurgicaux || ''}
                      onChange={(e) => setEditData({ ...editData, antecedents_chirurgicaux: e.target.value })}
                      className={inputClass + ' resize-none'}
                      rows={2}
                      maxLength={500}
                    />
                  ) : (
                    <p className="text-gray-900">{patient.antecedents_chirurgicaux || '-'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Médicaments Actuels */}
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Médicaments Actuels</h2>
              {editMode ? (
                <textarea
                  value={currentData.medicaments_actuels || ''}
                  onChange={(e) => setEditData({ ...editData, medicaments_actuels: e.target.value })}
                  className={inputClass + ' resize-none'}
                  rows={3}
                  maxLength={500}
                />
              ) : (
                <p className="text-gray-900">{patient.medicaments_actuels || '-'}</p>
              )}
            </div>

            {/* Personne à Contacter */}
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Personne à Contacter en Cas d'Urgence</h2>
              <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                <div>
                  <p className={labelClass}>Nom</p>
                  {editMode ? (
                    <input
                      type="text"
                      value={currentData.personne_contact_nom || ''}
                      onChange={(e) => setEditData({ ...editData, personne_contact_nom: e.target.value })}
                      className={inputClass}
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{patient.personne_contact_nom || '-'}</p>
                  )}
                </div>
                <div>
                  <p className={labelClass}>Téléphone</p>
                  {editMode ? (
                    <input
                      type="text"
                      value={currentData.personne_contact_telephone || ''}
                      onChange={(e) => setEditData({ ...editData, personne_contact_telephone: e.target.value })}
                      className={inputClass}
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{patient.personne_contact_telephone || '-'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Notes Médicales */}
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes Médicales</h2>
              {editMode ? (
                <textarea
                  value={currentData.notes_medicales || ''}
                  onChange={(e) => setEditData({ ...editData, notes_medicales: e.target.value })}
                  className={inputClass + ' resize-none'}
                  rows={4}
                  maxLength={1000}
                />
              ) : (
                <p className="text-gray-900 whitespace-pre-wrap">{patient.notes_medicales || '-'}</p>
              )}
            </div>

            {/* Boutons d'édition */}
            {editMode && (
              <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <button
                    type="button"
                    onClick={cancelEditing}
                    className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors font-semibold text-sm whitespace-nowrap cursor-pointer"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2.5 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue-dark transition-all font-semibold text-sm shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sauvegarde...
                      </>
                    ) : (
                      <>
                                                Enregistrer les modifications
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'dossier_rose' && showDossierRose && (
          <DossierRoseForm
            patientId={patientId!}
            userId={user!.id}
            onSaved={() => {}}
          />
        )}

        {activeTab === 'historique' && (
          <HistoriqueSection patientId={patientId!} patientUserRole={role || undefined} />
        )}

        {activeTab === 'partages' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <i className="ri-share-line text-6xl text-gray-300"></i>
            <p className="mt-4 text-gray-600">Aucun partage</p>
          </div>
        )}
      </div>

      {/* Modal sélection de consultation pour fiche de visite */}
      {showFicheVisitePicker && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={() => setShowFicheVisitePicker(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Choisir une hospitalisation</h3>
              <button
                onClick={() => setShowFicheVisitePicker(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto max-h-[60vh]">
              <p className="text-sm text-gray-500 mb-3">Sélectionnez la consultation pour laquelle générer la fiche de visite :</p>
              {recentConsultations.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    navigate(`/medecin/fiche-visite?patientId=${patientId}&consultationId=${c.id}`);
                    setShowFicheVisitePicker(false);
                  }}
                  className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-clinic-blue/40 hover:bg-clinic-blue/5 transition-all cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {new Date(c.date_consultation).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {c.statut === 'termine' ? 'Hospitalisation terminée' : 'Hospitalisation en cours'}
                      </p>
                    </div>
                    <i className="ri-arrow-right-line text-clinic-blue"></i>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}